import type {DividerProps} from '@/components/Divider';
import type {SpacingValue} from '@travel-frontend/themes';

import {useResizeObserver} from '@react-hookz/web';
import {Fragment, useRef} from 'react';

import {Divider} from '@/components/Divider';
import {Container} from '@/components/SegmentedList/components/Container';
import {ListItem} from '@/components/SegmentedList/components/ListItem';

type Props = {
  alignment?: 'right' | 'left';
  items: Array<{key: string | number; element: React.ReactNode}>;
  rowSpacing?: SpacingValue<'rowGap'>;
  columnSpacing?: SpacingValue<'columnGap'>;
} & Omit<DividerProps, 'orientation'>;

const HIDE = 'none';
const SHOW = 'inline-block';

export const SegmentedList = ({
  alignment = 'left',
  items,
  rowSpacing = 'lists/small',
  columnSpacing = 'lists/medium',
  ...dividerProps
}: Props) => {
  const ref = useRef<HTMLUListElement>(null);

  useResizeObserver(
    ref,
    (entry) => {
      entry.target.childNodes
        // eslint-disable-next-line unicorn/no-array-for-each -- forEach is just easier to read in this case
        .forEach((element, index, listOfChildren) => {
          const previousElement = listOfChildren[index - 1];

          if (
            index === 0 ||
            !(
              element instanceof HTMLElement &&
              previousElement instanceof HTMLElement
            )
          ) {
            return;
          }

          // if element is more to the left than it's previous element it means it wrapped to a new line
          const isWrapping = element.offsetTop > previousElement.offsetTop;

          if (element instanceof HTMLLIElement) {
            if (alignment === 'left') {
              previousElement.style.borderColor = isWrapping
                ? 'transparent'
                : '';
              if (element.nextElementSibling instanceof HTMLElement) {
                element.nextElementSibling.style.display = SHOW;
              }
            } else {
              previousElement.style.display = isWrapping ? HIDE : SHOW;
            }
          } else {
            element.style.display = isWrapping ? HIDE : SHOW;
          }
        });
    },
    true
  );

  return (
    <Container
      as="ul"
      // @ts-expect-error ...
      ref={ref}
      columnGap={columnSpacing}
      rowGap={rowSpacing}
      justifyContent={alignment === 'right' ? 'flex-end' : 'flex-start'}
    >
      {items.map((item, index) => (
        <Fragment key={item.key}>
          <ListItem style={{textAlign: alignment}}>{item.element}</ListItem>
          {index !== items.length - 1 && (
            <Divider
              aria-hidden="true"
              {...dividerProps}
              orientation="vertical"
            />
          )}
        </Fragment>
      ))}
    </Container>
  );
};
